import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { CodeInjection } from "@/components/layout/CodeInjection";
import { ChatBubble } from "@/components/chat/ChatBubble";
import { AuthProvider } from "@/hooks/useAuth";

const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Careers = lazy(() => import("./pages/Careers"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Locations
const Locations = lazy(() => import("./pages/Locations"));
const LocationPage = lazy(() => import("./pages/locations/LocationPage"));

// Services
const Services = lazy(() => import("./pages/Services"));
const ServicePanels = lazy(() => import("./pages/services/ServicePanels"));
const EVChargers = lazy(() => import("./pages/services/EVChargers"));
const ElectricalRemodeling = lazy(() => import("./pages/services/ElectricalRemodeling"));
const CommercialServices = lazy(() => import("./pages/services/CommercialServices"));
const OutdoorLights = lazy(() => import("./pages/services/OutdoorLights"));

// Projects
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <CodeInjection />
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/admin" element={
                <Suspense fallback={<div className="min-h-screen" />}>
                  <Admin />
                </Suspense>
              } />
              
              <Route path="*" element={
                <div className="flex flex-col min-h-screen">
                  <Navbar />
                  <div className="flex-1">
                    <Suspense fallback={<div className="min-h-screen" />}>
                      <Routes>
                        <Route path="/" element={<Index />} />
                        
                        {/* Main Routes */}
                        <Route path="/about" element={<About />} />
                        <Route path="/careers" element={<Careers />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />
                        
                        {/* Services */}
                        <Route path="/services" element={<Services />} />
                        <Route path="/services/service-panels" element={<ServicePanels />} />
                        <Route path="/services/ev-chargers" element={<EVChargers />} />
                        <Route path="/services/electrical-remodeling" element={<ElectricalRemodeling />} />
                        <Route path="/services/commercial" element={<CommercialServices />} />
                        <Route path="/services/outdoor-lights" element={<OutdoorLights />} />
                        
                        {/* Projects */}
                        <Route path="/projects" element={<Projects />} />
                        <Route path="/projects/:slug" element={<ProjectDetail />} />
                        
                        {/* Locations */}
                        <Route path="/locations" element={<Locations />} />
                        <Route path="/locations/:slug" element={<LocationPage />} />
                        
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </div>
                  <Footer />
                  <ChatBubble />
                </div>
              } />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
